import { Box } from "@chakra-ui/react";
import { useContext } from "react";
import { FormattedMessage, useIntl } from "react-intl";

import { TenantContext } from "@nens/ranalib";
import { RanaModal } from "@nens/ranalib";

import { useRouteBlocker } from "@/hooks/use-route-blocker";

import { PUBLICATION_SEARCH_KEY } from "../../../";
import { getIsChanged, getIsEditable } from "../../../store/selectors";
import { usePublicationStore } from "../../../store/store";
import { SaveChangesButton } from "../../action-bar/components/save-changes-button";

export function PublicationRouteBlocker() {
  const isEditable = usePublicationStore(getIsEditable);
  const isChanged = usePublicationStore(getIsChanged);
  const { isPublic } = useContext(TenantContext);
  const intl = useIntl();

  const { proceed, reset, state } = useRouteBlocker((args) => {
    // Never block if there are no unsaved changes or if the publication is public
    if (!isChanged || isPublic) return false;

    // Block if the event is a beforeunload event (refresh, closing tab)
    if ("eventType" in args) {
      if (args.eventType === "beforeunload") {
        return true;
      }

      // At the time of writing this code "beforeunload" is the only eventType that is supported, but we add this warning to be future proof
      console.warn("Unknown event type in route blocker", args.eventType);
      return false;
    }

    const { currentLocation, nextLocation } = args;

    const currentParams = new URLSearchParams(currentLocation.search);
    const nextParams = new URLSearchParams(nextLocation.search);
    const currentPublicationId = currentParams.get(PUBLICATION_SEARCH_KEY);
    const nextPublicationId = nextParams.get(PUBLICATION_SEARCH_KEY);

    // Only block in a few scenario's
    // - Navigating to a different page
    // - Navigating to a different publication
    // We don't check any other query params then the publication id to prevent blocking stuff like opening the map or selecting a comment
    const pageChanged = currentLocation.pathname !== nextLocation.pathname;
    const publicationIdChanged = currentPublicationId !== nextPublicationId;

    if (pageChanged || publicationIdChanged) {
      return true;
    }

    return false;
  });

  if (state !== "blocked") return null;

  return (
    <RanaModal
      topBar={
        <FormattedMessage
          id="header.leave_page"
          defaultMessage="Do you really want to leave this page?"
        />
      }
      onClose={reset}
      buttons={[
        {
          children: <FormattedMessage id="common.cancel" defaultMessage="Cancel" />,
          variant: "secondary",
          onClick: reset,
        },
        {
          children: <FormattedMessage id="common.yes" defaultMessage="Yes" />,
          variant: "danger",
          onClick: proceed,
        },
        <SaveChangesButton
          onSuccess={proceed}
          buttonText={intl.formatMessage({
            id: "publication.leave_page_save_changes",
            defaultMessage: "Yes, but save changes",
          })}
          canSaveChanges
          key="save-changes-button"
        />,
      ]}
      isOpen
    >
      <Box>
        {isEditable ? (
          <FormattedMessage
            id="publication.unsaved_changes"
            defaultMessage="You have unsaved changes. Are you sure you want to leave?"
          />
        ) : (
          <FormattedMessage
            id="publication.unsaved_changes_save_hint"
            defaultMessage="You have changed a default value (likely a layers visibility). {lineBreak} Are you sure you want to leave without saving?"
            values={{ lineBreak: <br /> }}
          />
        )}
      </Box>
    </RanaModal>
  );
}
